import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { updateZettel, createZettel, uploadImage } from '../api';
import LiveEditor from './LiveEditor';
import { getDirectChildren, sortByLuhmann, getParentId } from '../utils/luhmann';

// Modul-Konstante: Regex wird nur einmal kompiliert statt bei jedem Render
const DETAILS_REGEX_SRC = '(?:<details(?:\\s+data-ref="([^"]*)")?\\s*>\\s*\\n<summary>(.*?)<\\/summary>\\s*\\n([\\s\\S]*?)\\n<\\/details>)';

// uploadImage ist in api.js definiert — hier nicht mehr nötig

function ZettelEditor({ zettel, isEditing, setIsEditing, onSaved, onNavigate, allZettel, onOpenReadwise, linkingHighlight, onLinkToExisting, flushSaveRef, highlightPatch, onHighlightDrop, getHighlightData, newNoteMode, onNewNoteSaved, onHighlightUnlinked, onHighlightLinked, editorFocusKey, onFocusPanel, readOnly = false }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [links, setLinks] = useState('');
  const editorRef = useRef(null);
  const titleInputRef = useRef(null);
  const [parentId, setParentId] = useState('');
  const [parentSearch, setParentSearch] = useState('');
  const [topLevel, setTopLevel] = useState(false);
  const textareaRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const saveStatusTimerRef = useRef(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const onHighlightLinkedRef = useRef(onHighlightLinked);
  onHighlightLinkedRef.current = onHighlightLinked;
  const onHighlightUnlinkedRef = useRef(onHighlightUnlinked);
  onHighlightUnlinkedRef.current = onHighlightUnlinked;

  // --- Highlight Drop in Editor (Hooks MÜSSEN vor Early Returns stehen!) ---
  const [editorHlDragOver, setEditorHlDragOver] = useState(false);
  const handleEditorHlDragOver = useCallback((e) => {
    if (!e.dataTransfer.types.includes('application/highlight-ref')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!editorHlDragOver) setEditorHlDragOver(true);
  }, [editorHlDragOver]);

  const handleEditorHlDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setEditorHlDragOver(false);
    }
  }, []);

  const handleEditorHlDrop = useCallback((e) => {
    if (!e.dataTransfer.types.includes('application/highlight-ref')) return;
    e.preventDefault();
    e.stopPropagation();
    setEditorHlDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/highlight-ref'));
      if (onHighlightDrop && zettel) {
        onHighlightDrop(data, { type: 'editor', zettelId: zettel.id });
      }
    } catch (err) { console.error('Editor-HL-Drop-Fehler:', err); }
  }, [zettel, onHighlightDrop]);

  // Native Drag & Drop
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    const onDragOver = (e) => {
      if (e.dataTransfer && e.dataTransfer.types.indexOf('Files') !== -1) {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const onDrop = async (e) => {
      const files = [...e.dataTransfer.files].filter(f => f.type.startsWith('image/'));
      if (files.length === 0) return;
      e.preventDefault();
      e.stopPropagation();

      const pos = ta.selectionStart ?? 0;
      for (const file of files) {
        try {
          const filename = await uploadImage(file);
          const md = `\n![${file.name.replace(/\.[^.]+$/, '')}](${filename})\n`;
          setContent(prev => prev.slice(0, pos) + md + prev.slice(pos));
        } catch (err) {
          console.error('Bild-Upload fehlgeschlagen:', err);
        }
      }
    };

    ta.addEventListener('dragover', onDragOver, true);
    ta.addEventListener('drop', onDrop, true);
    return () => {
      ta.removeEventListener('dragover', onDragOver, true);
      ta.removeEventListener('drop', onDrop, true);
    };
  });

  useEffect(() => {
    if (zettel) {
      setTitle(zettel.title || '');
      const rawContent = zettel.content || '';
      // Migration: <details data-ref="REF">...</details> → {{highlight:REF}} (nur wenn nötig)
      if (rawContent.includes('<details') && rawContent.includes('data-ref=')) {
        const detailsMigrationRegex = /(?:\n---\n\s*)?<details\s+data-ref="([^"]*)"[^>]*>\s*\n\s*<summary>[^<]*<\/summary>\s*\n[\s\S]*?\n\s*<\/details>/g;
        const migrated = rawContent.replace(detailsMigrationRegex, (match, ref) => {
          return `\n{{highlight:${ref}}}`;
        });
        const normalized = migrated.replace(/\n{3,}/g, '\n\n').trimEnd();
        setContent(normalized);
        if (normalized !== rawContent) {
          updateZettel(zettel.id, { content: normalized }).catch(() => {});
        }
      } else {
        setContent(rawContent);
      }
      setTags((zettel.tags || []).join(', '));
      setLinks((zettel.links || []).join(', '));
    }
  }, [zettel]);

  // Echtzeit-Sync: Mit {{highlight:ref}} Markern nicht mehr nötig —
  // Der Widget liest die Daten direkt aus dem Store via getHighlightData()

  // Wrap-Shortcuts
  const handleTextareaKeyDown = (e) => {
    const ta = e.target;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const hasSelection = start !== end;

    if (e.key === '=' && hasSelection) {
      e.preventDefault();
      const selected = content.slice(start, end);
      if (content[start - 1] === '=' && content[end] === '=') {
        setContent(content.slice(0, start - 1) + '==' + selected + '==' + content.slice(end + 1));
        setTimeout(() => { ta.selectionStart = start + 1; ta.selectionEnd = end + 1; }, 0);
      } else {
        setContent(content.slice(0, start) + '=' + selected + '=' + content.slice(end));
        setTimeout(() => { ta.selectionStart = start + 1; ta.selectionEnd = end + 1; }, 0);
      }
      return;
    }
    if (!hasSelection) return;

    const cmd = e.metaKey || e.ctrlKey;
    let wrap = null;
    if (cmd && e.key === 'b') wrap = '**';
    else if (cmd && e.key === 'i') wrap = '*';
    else if (e.key === '*') wrap = '*';

    if (!wrap) return;
    e.preventDefault();
    const selected = content.slice(start, end);
    setContent(content.slice(0, start) + wrap + selected + wrap + content.slice(end));
    setTimeout(() => { ta.selectionStart = start + wrap.length; ta.selectionEnd = end + wrap.length; }, 0);
  };

  // Linking state
  const [linkOption, setLinkOption] = useState('existing');
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [selectedLinkZettel, setSelectedLinkZettel] = useState(null);

  // Auto-Save
  const autoSaveRef = useRef(null);
  useEffect(() => {
    if (!zettel) return;
    const changed = title !== zettel.title || content !== zettel.content;
    if (!changed) return;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    setSaveStatus('saving');
    autoSaveRef.current = setTimeout(() => {
      // Highlight-Marker im Content erkennen
      const markerRegex = /\{\{highlight:([\w-]+)\}\}/g;
      const currentRefs = [];
      let m;
      while ((m = markerRegex.exec(content)) !== null) currentRefs.push(m[1]);
      const existingRefs = Array.isArray(zettel.highlight_ref) ? zettel.highlight_ref : (zettel.highlight_ref ? [zettel.highlight_ref] : []);
      const newRefs = currentRefs.filter(ref => !existingRefs.includes(ref));
      const removedRefs = existingRefs.filter(ref => !currentRefs.includes(ref));
      const updateData = {
        title, content,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        links: links.split(',').map(l => l.trim()).filter(Boolean),
      };
      // Erste neue Ref direkt mitschicken, weitere nacheinander registrieren
      if (newRefs.length > 0) updateData.highlight_ref = newRefs[0];
      updateZettel(zettel.id, updateData).then(saved => {
        // Restliche Refs nachregistrieren
        if (newRefs.length > 1) {
          Promise.all(newRefs.slice(1).map(ref => updateZettel(zettel.id, { highlight_ref: ref }))).catch(err => console.error('Highlight-Refs registrieren fehlgeschlagen:', err));
        }
        // Entfernte Refs automatisch unlinken (Marker wurde im Editor gelöscht)
        if (removedRefs.length > 0) {
          Promise.all(removedRefs.map(ref =>
            updateZettel(zettel.id, { remove_highlight_ref: ref })
          )).then(() => {
            // ReadwisePanel + Graph über entfernten Link informieren
            if (onHighlightUnlinkedRef.current) {
              removedRefs.forEach(ref => onHighlightUnlinkedRef.current(zettel.id, ref));
            }
          }).catch(err => console.error('Highlight-Refs entfernen fehlgeschlagen:', err));
        }
        onSavedRef.current(saved);
        // Echtzeit-Link: ReadwisePanel sofort über neue Verlinkung informieren
        if (newRefs.length > 0 && onHighlightLinkedRef.current) {
          newRefs.forEach(ref => onHighlightLinkedRef.current(zettel.id, ref));
        }
        setSaveStatus('saved');
        clearTimeout(saveStatusTimerRef.current);
        saveStatusTimerRef.current = setTimeout(() => setSaveStatus(null), 2000);
      });
    }, 400);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [title, content]); // eslint-disable-line

  // Cleanup save status timer
  useEffect(() => {
    return () => {
      if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
    };
  }, []);

  // New-note-mode or _focusTitle: auto-focus title input
  useEffect(() => {
    if ((newNoteMode || zettel?._focusTitle) && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [newNoteMode, zettel?._focusTitle, zettel?.id]);

  // ⌘3: Editor fokussieren — Cursor im CM-Editor aktivieren
  useEffect(() => {
    if (editorFocusKey > 0 && zettel) {
      // Titel vorhanden → CM-Editor fokussieren, sonst Titel-Input
      if (zettel.title && editorRef.current) {
        editorRef.current.focus();
      } else if (titleInputRef.current) {
        titleInputRef.current.focus();
      }
    }
  }, [editorFocusKey]); // eslint-disable-line

  // Expose flush function: immediately save pending changes
  const titleRef = useRef(title);
  const contentRef = useRef(content);
  const tagsRef = useRef(tags);
  const linksRef = useRef(links);
  titleRef.current = title;
  contentRef.current = content;
  tagsRef.current = tags;
  linksRef.current = links;

  useEffect(() => {
    if (!flushSaveRef) return;
    flushSaveRef.current = () => {
      if (!zettel) return;
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current);
        autoSaveRef.current = null;
      }
      const changed = titleRef.current !== zettel.title || contentRef.current !== zettel.content;
      if (!changed) return;
      // Fire-and-forget — synchronous trigger, async save
      updateZettel(zettel.id, {
        title: titleRef.current,
        content: contentRef.current,
        tags: tagsRef.current.split(',').map(t => t.trim()).filter(Boolean),
        links: linksRef.current.split(',').map(l => l.trim()).filter(Boolean),
      }).then(saved => onSaved(saved));
    };
  });

  useEffect(() => {
    if (linkingHighlight) {
      setLinkOption('existing');
      setLinkSearchQuery('');
      setSelectedLinkZettel(null);
      setTitle('');
      setContent(`\n<details data-ref="${linkingHighlight.ref}">\n<summary>${linkingHighlight.author}: ${linkingHighlight.title}</summary>\n\n> "${linkingHighlight.text}"\n\n</details>`);
      setTags('');
      setParentId('');
      setParentSearch('');
    }
  }, [linkingHighlight]);

  if (!zettel && !linkingHighlight) {
    return (
      <div className="zettel-editor" tabIndex={-1} onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          // Zurück zum Zettelkasten
          if (onFocusPanel) onFocusPanel('tree');
        }
      }}>
        <div className="editor-empty">
          {/* Zettelkasten illustration — wooden box with protruding cards */}
          <div className="empty-illustration">
            <svg width="260" height="220" viewBox="0 0 260 220" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Shadow under box */}
              <ellipse cx="130" cy="200" rx="90" ry="8" fill="var(--color-text-tertiary)" opacity="0.08" className="empty-box-shadow"/>

              {/* Box body — warm wood tones */}
              <rect x="50" y="100" width="160" height="90" rx="6" fill="var(--color-surface-2)" stroke="var(--color-divider)" strokeWidth="1.5"/>
              {/* Wood grain lines */}
              <line x1="60" y1="130" x2="200" y2="130" stroke="var(--color-divider)" strokeWidth="0.5" opacity="0.4"/>
              <line x1="60" y1="155" x2="200" y2="155" stroke="var(--color-divider)" strokeWidth="0.5" opacity="0.3"/>
              {/* Box front label area */}
              <rect x="105" y="140" width="50" height="24" rx="3" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="0.8" opacity="0.25"/>

              {/* Tab dividers inside box */}
              <rect x="85" y="92" width="3" height="16" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.2"/>
              <rect x="115" y="90" width="3" height="18" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.25"/>
              <rect x="150" y="91" width="3" height="17" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.2"/>
              <rect x="180" y="93" width="3" height="15" rx="1.5" fill="var(--color-text-tertiary)" opacity="0.15"/>

              {/* Card 1 — peeking out left, slightly tilted */}
              <g className="empty-card empty-card-1">
                <rect x="68" y="28" width="52" height="76" rx="4" fill="var(--color-surface)" stroke="var(--color-divider)" strokeWidth="1" transform="rotate(-6 94 66)"/>
                <rect x="78" y="42" width="28" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.35" transform="rotate(-6 92 42)"/>
                <rect x="78" y="48" width="22" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.25" transform="rotate(-6 89 48)"/>
                <rect x="78" y="54" width="26" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.2" transform="rotate(-6 91 54)"/>
              </g>

              {/* Card 2 — center, sticking up tall (the "active" one) */}
              <g className="empty-card empty-card-2">
                <rect x="108" y="14" width="52" height="88" rx="4" fill="var(--color-surface)" stroke="var(--color-divider)" strokeWidth="1.2"/>
                {/* Title line */}
                <rect x="118" y="28" width="32" height="2" rx="1" fill="var(--color-text-secondary)" opacity="0.5"/>
                {/* Divider */}
                <line x1="118" y1="36" x2="150" y2="36" stroke="var(--color-divider)" strokeWidth="0.8" opacity="0.4"/>
                {/* Content lines */}
                <rect x="118" y="42" width="28" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.35"/>
                <rect x="118" y="48" width="32" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.3"/>
                <rect x="118" y="54" width="24" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.25"/>
                <rect x="118" y="60" width="30" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.2"/>
                {/* Small link indicator */}
                <circle cx="152" cy="28" r="3" fill="none" stroke="var(--color-action)" strokeWidth="0.8" opacity="0.4"/>
                <path d="M150.5 28 L153.5 28 M152 26.5 L152 29.5" stroke="var(--color-action)" strokeWidth="0.6" opacity="0.4"/>
              </g>

              {/* Card 3 — peeking right, slight tilt */}
              <g className="empty-card empty-card-3">
                <rect x="148" y="36" width="48" height="68" rx="4" fill="var(--color-surface)" stroke="var(--color-divider)" strokeWidth="1" transform="rotate(4 172 70)"/>
                <rect x="158" y="50" width="24" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.3" transform="rotate(4 170 50)"/>
                <rect x="158" y="56" width="20" height="1.5" rx="0.75" fill="var(--color-text-tertiary)" opacity="0.2" transform="rotate(4 168 56)"/>
              </g>

              {/* Pen resting against box — right side */}
              <g className="empty-pen" opacity="0.4">
                <line x1="218" y1="82" x2="228" y2="192" stroke="var(--color-text-secondary)" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="218" y1="82" x2="216.5" y2="76" stroke="var(--color-action)" strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
              </g>

              {/* Floating knowledge particles */}
              <circle cx="42" cy="50" r="2" fill="var(--color-action)" opacity="0.15" className="empty-dot empty-dot-1"/>
              <circle cx="224" cy="40" r="2.5" fill="var(--color-action)" opacity="0.12" className="empty-dot empty-dot-2"/>
              <circle cx="38" cy="140" r="1.5" fill="var(--color-action)" opacity="0.15" className="empty-dot empty-dot-3"/>

              {/* Subtle connection arc between cards */}
              <path d="M100 50 Q130 20 148 48" stroke="var(--color-action)" strokeWidth="0.8" strokeDasharray="3 4" opacity="0.15" className="empty-connection"/>
            </svg>
          </div>
          <p className="editor-empty-text">Wähle einen Zettel aus dem Zettelkasten, um ihn hier zu bearbeiten.</p>
          <span className="editor-empty-hint">⌘2 Zettelkasten</span>
        </div>
      </div>
    );
  }

  // --- Linking Panel ---
  if (!zettel && linkingHighlight) {
    const detailsBlock = `<details data-ref="${linkingHighlight.ref}">\n<summary>${linkingHighlight.author}: ${linkingHighlight.title}</summary>\n\n> "${linkingHighlight.text}"\n\n</details>`;

    const linkMatches = linkSearchQuery.trim()
      ? allZettel.filter(z => z.id.includes(linkSearchQuery) || z.title?.toLowerCase().includes(linkSearchQuery.toLowerCase())).slice(0, 8)
      : allZettel.slice(0, 8);

    const parentMatches = parentSearch.trim()
      ? allZettel.filter(z => z.id.includes(parentSearch) || z.title?.toLowerCase().includes(parentSearch.toLowerCase())).slice(0, 6)
      : [];
    const selectedParent = parentId ? allZettel.find(z => z.id === parentId) : null;

    const canSaveNew = title.trim() && (parentId || linkOption === 'newIndex');

    const handleCreateSave = async () => {
      if (!canSaveNew) return;
      const newZettel = await createZettel({
        title: title.trim(), content, type: 'permanent',
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        links: [], source: `${linkingHighlight.author}: ${linkingHighlight.title}`,
        highlight_ref: linkingHighlight.ref,
        ...(parentId ? { parentId } : {})
      });
      onSaved();
      if (newZettel?.id) onNavigate(newZettel.id);
    };

    const focusContent = () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.selectionStart = 0;
        textareaRef.current.selectionEnd = 0;
      }
    };

    return (
      <div className="zettel-editor">
        <div className="editor-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
            <button className={`link-option-btn${linkOption === 'existing' ? ' active' : ''}`} onClick={() => { setLinkOption('existing'); setSelectedLinkZettel(null); }}>
              In vorhandenem Zettel einfügen
            </button>
            <button className={`link-option-btn${linkOption === 'underIndex' ? ' active' : ''}`} onClick={() => { setLinkOption('underIndex'); setSelectedLinkZettel(null); }}>
              Neuen Zettel unter Index erstellen
            </button>
            <button className={`link-option-btn${linkOption === 'newIndex' ? ' active' : ''}`} onClick={() => { setLinkOption('newIndex'); setSelectedLinkZettel(null); }}>
              Neuen Index-Strang erstellen
            </button>
          </div>

          {linkOption === 'existing' && (
            <div>
              <input value={linkSearchQuery} onChange={e => { setLinkSearchQuery(e.target.value); setSelectedLinkZettel(null); }}
                placeholder="Zettel suchen (ID oder Titel)..."
                className="editor-tags-input" style={{ marginTop: 0, marginBottom: 8 }} />

              {!selectedLinkZettel && (
                <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 12 }}>
                  {linkMatches.map(z => (
                    <button key={z.id} className="link-search-result" onClick={() => setSelectedLinkZettel(z)}>
                      <span className="link-search-result-id">{z.id}</span>
                      {z.title || '(Ohne Titel)'}
                      {z.snippet && <div className="link-search-result-snippet">{z.snippet}</div>}
                    </button>
                  ))}
                </div>
              )}

              {selectedLinkZettel && (
                <div className="link-selected-card">
                  <div className="link-selected-card-header">
                    <div>
                      <span className="link-search-result-id">{selectedLinkZettel.id}</span>
                      <span style={{ fontWeight: 600 }}>{selectedLinkZettel.title || '(Ohne Titel)'}</span>
                    </div>
                    <button onClick={() => setSelectedLinkZettel(null)} className="link-selected-card-close">✕</button>
                  </div>
                  {selectedLinkZettel.snippet && (
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontStyle: 'italic', lineHeight: 1.5 }}>
                      {selectedLinkZettel.snippet}
                    </div>
                  )}
                  <button onClick={() => onLinkToExisting(selectedLinkZettel.id)} className="btn-save" style={{ marginTop: 12 }}>
                    Highlight in Zettel {selectedLinkZettel.id} einfügen
                  </button>
                </div>
              )}
            </div>
          )}

          {linkOption === 'underIndex' && (
            <div>
              <div style={{ marginBottom: 12 }}>
                {selectedParent ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="parent-selected-chip">
                      <span className="link-search-result-id">{selectedParent.id}</span>{selectedParent.title || '(Ohne Titel)'}
                    </div>
                    <button onClick={() => { setParentId(''); setParentSearch(''); }} className="link-selected-card-close">✕</button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input value={parentSearch} onChange={e => setParentSearch(e.target.value)} placeholder="Unter welchem Index? (ID oder Titel)..."
                      className="editor-tags-input" style={{ marginTop: 0 }} />
                    {parentMatches.length > 0 && (
                      <div className="parent-dropdown">
                        {parentMatches.map(z => (
                          <button key={z.id} className="parent-dropdown-item" onClick={() => { setParentId(z.id); setParentSearch(''); }}>
                            <span className="link-search-result-id">{z.id}</span>{z.title || '(Ohne Titel)'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <input className="editor-title-input" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Titel deines Gedankens..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); focusContent(); } }} />
              <LiveEditor value={content} onChange={setContent} placeholder="Dein Gedanke..." />
              <button onClick={handleCreateSave} disabled={!canSaveNew} className="btn-save" style={{ marginTop: 16, opacity: canSaveNew ? 1 : 0.4 }}>Zettel erstellen</button>
            </div>
          )}

          {linkOption === 'newIndex' && (
            <div>
              <div style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)', fontStyle: 'italic', marginBottom: 12 }}>
                Erstellt einen neuen Hauptstrang als Übersichtsseite.
              </div>
              <input className="editor-title-input" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Titel deines Gedankens..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); focusContent(); } }} />
              <LiveEditor value={content} onChange={setContent} placeholder="Dein Gedanke..." />
              <button onClick={handleCreateSave} disabled={!title.trim()} className="btn-save" style={{ marginTop: 16, opacity: title.trim() ? 1 : 0.4 }}>Zettel erstellen</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    const saved = await updateZettel(zettel.id, {
      title, content,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      links: links.split(',').map(l => l.trim()).filter(Boolean),
    });
    setIsEditing(false);
    onSaved(saved);
  };


  const handleBranch = async () => {
    const newZettel = await createZettel({
      title: '', content: '', tags: [], links: [zettel.id],
      type: 'permanent', parentId: zettel.id
    });
    onSaved();
    onNavigate(newZettel.id);
  };

  // Highlight-Marker aus dem Zettel entfernen — sofort im Editor sichtbar
  const removeHighlightFromContent = useCallback((sourceContent, ref) => {
    const escaped = ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // {{highlight:ref}} Einzeiler entfernen (ggf. mit vorangehendem ---\n)
    const markerRegex = new RegExp(`(\\n---\\n)?\\s*\\{\\{highlight:${escaped}\\}\\}`, 'g');
    return sourceContent
      .replace(markerRegex, '')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/^\n---\n/, '')
      .replace(/\n---\n\s*$/, '')
      .trimEnd();
  }, []);

  // Sofortige Entfernung (ohne Bestätigung) — wird von Drag-out und Bibliothek-Sync genutzt
  const doRemoveHighlight = useCallback(async (ref) => {
    if (!zettel || !ref) return;
    const newContent = removeHighlightFromContent(content, ref);
    // Optimistic UI-Update, dann Server-Sync mit Rollback bei Fehler
    const prevContent = content;
    setContent(newContent);
    // Sofort Bibliothek informieren — Link-Chip verschwindet ohne Reload
    if (onHighlightUnlinked) onHighlightUnlinked(zettel.id, ref);
    try {
      // Nur die spezifische Ref entfernen, nicht alle highlight_refs löschen
      const saved = await updateZettel(zettel.id, { content: newContent, remove_highlight_ref: ref });
      onSaved(saved);
    } catch (err) {
      console.error('Fehler beim Entfernen des Highlights:', err);
      setContent(prevContent); // Rollback bei Fehler
    }
  }, [zettel, content, onSaved, removeHighlightFromContent, onHighlightUnlinked]);

  // Manuelle Entfernung (mit Bestätigung) — wird vom Trash-Button aufgerufen
  const handleRemoveHighlight = useCallback(async (ref) => {
    if (!zettel || !ref) return;
    if (!window.confirm('Eingebettetes Highlight aus dieser Notiz entfernen?')) return;
    doRemoveHighlight(ref);
  }, [zettel, doRemoveHighlight]);

  // Highlight innerhalb des Editors verschieben oder per Drag-out löschen
  const handleMoveHighlight = useCallback(async (ref, targetRef, targetLineText) => {
    if (!zettel || !ref) return;
    const marker = `{{highlight:${ref}}}`;

    // Marker entfernen
    let cleaned = removeHighlightFromContent(content, ref);

    if (targetRef) {
      // Vor einem anderen Highlight-Marker einfügen
      const targetMarker = `{{highlight:${targetRef}}}`;
      const targetIdx = cleaned.indexOf(targetMarker);
      if (targetIdx >= 0) {
        cleaned = cleaned.slice(0, targetIdx) + marker + '\n' + cleaned.slice(targetIdx);
      } else {
        cleaned = cleaned.trimEnd() + '\n' + marker;
      }
    } else if (targetLineText != null) {
      // Einfügen vor einer bestimmten Zeile (Zeilentext als stabiler Anker)
      const lines = cleaned.split('\n');
      const idx = lines.findIndex(l => l === targetLineText);
      if (idx >= 0) {
        lines.splice(idx, 0, marker);
        cleaned = lines.join('\n');
      } else {
        cleaned = cleaned.trimEnd() + '\n' + marker;
      }
    } else {
      // Am Ende einfügen
      cleaned = cleaned.trimEnd() + '\n' + marker;
    }

    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trimEnd();
    setContent(cleaned);
    const saved = await updateZettel(zettel.id, { content: cleaned });
    onSaved(saved);
  }, [zettel, content, onSaved, removeHighlightFromContent]);

  // Render Content with blockquotes, [[wiki-links]] and <details>
  // useCallback: Regex nicht bei jedem Render neu kompilieren
  const renderContent = useCallback((text) => {
    if (!text) return null;

    const detailsRegex = new RegExp(DETAILS_REGEX_SRC, 'g');
    const segments = [];
    let lastIndex = 0;
    let match;
    while ((match = detailsRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      segments.push({ type: 'details', ref: match[1] || null, summary: match[2], content: match[3].trim() });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      segments.push({ type: 'text', content: text.slice(lastIndex) });
    }

    if (segments.some(s => s.type === 'details')) {
      return (
        <div>
          {segments.map((seg, i) => {
            if (seg.type === 'details') {
              return (
                <details key={`det-${i}`} style={{
                  margin: '6px 0', border: 'none', borderLeft: '3px solid color-mix(in srgb, var(--color-primary) 35%, transparent)',
                  background: 'transparent', overflow: 'hidden', transition: 'border-color 0.15s'
                }}>
                  <summary style={{
                    padding: '4px 0 4px 14px', cursor: 'pointer', fontSize: 'var(--text-sm)',
                    color: 'var(--color-text-tertiary)', fontWeight: 500,
                    listStyle: 'none', display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'color 0.15s'
                  }}>
                    <span style={{ fontSize: '0.6em', transition: 'transform 0.15s', opacity: 0.6 }}>▶</span>
                    <span style={{ flex: 1 }}>{seg.summary}</span>
                    {seg.ref && (
                      <span
                        className="hl-embed-remove-btn"
                        title="Highlight entfernen"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveHighlight(seg.ref); }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </span>
                    )}
                  </summary>
                  <div style={{ padding: '6px 0 8px 14px', fontSize: 'var(--text-base)', lineHeight: 1.75 }}>
                    {renderContentInner(seg.content)}
                    {onOpenReadwise && (seg.ref || zettel?.highlight_ref) && (
                      <a href="#" className="highlight-source-link" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenReadwise(seg.ref || zettel.highlight_ref); }}>
                        Quelle in Bibliothek anzeigen
                      </a>
                    )}
                  </div>
                </details>
              );
            }
            return <div key={`seg-${i}`}>{renderContentInner(seg.content)}</div>;
          })}
        </div>
      );
    }

    return renderContentInner(text);
  }, [handleRemoveHighlight, onOpenReadwise, zettel]);

  const renderContentInner = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let bqLines = [];
    let listItems = [];
    let key = 0;

    const flushBlockquote = () => {
      if (bqLines.length === 0) return;
      elements.push(
        <blockquote key={`bq-${key++}`} className="highlight-card">
          {bqLines.map((l, j) => <span key={j}>{renderInline(l, `bq-l-${j}`)}{j < bqLines.length - 1 && <br />}</span>)}
        </blockquote>
      );
      bqLines = [];
    };

    const flushList = () => {
      if (listItems.length === 0) return;
      const isOrdered = listItems[0].ordered;
      const Tag = isOrdered ? 'ol' : 'ul';
      elements.push(
        <Tag key={`list-${key++}`} style={{
          margin: 'var(--space-3) 0',
          paddingLeft: 0,
          listStyle: 'none',
          paddingTop: 2,
          paddingBottom: 2
        }}>
          {listItems.map((item, j) => (
            <li key={j} style={{
              paddingLeft: (item.depth * 20) + 16,
              marginBottom: 4,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              lineHeight: 1.75
            }}>
              <span style={{
                color: 'var(--color-primary)',
                fontWeight: 700,
                flexShrink: 0,
                fontSize: item.ordered ? '0.8em' : (item.depth === 0 ? '0.85em' : '0.7em'),
                marginTop: item.ordered ? '0.22em' : (item.depth === 0 ? '0.22em' : '0.35em'),
                minWidth: item.ordered ? '1.4em' : 'auto',
                textAlign: item.ordered ? 'right' : 'left',
                fontFamily: item.ordered ? 'var(--font-mono)' : 'inherit',
                letterSpacing: item.ordered ? '-0.02em' : 'normal'
              }}>
                {item.ordered ? `${item.number}.` : (item.depth === 0 ? '●' : '○')}
              </span>
              <span style={{ flex: 1 }}>{renderInline(item.content, `li-${j}`)}</span>
            </li>
          ))}
        </Tag>
      );
      listItems = [];
    };

    const renderInline = (line, k) => {
      // Updated regex to match [[id]] and [[id|display text]]
      const parts = line.split(/(\!\[[^\]]*\]\([^)]+\)|\[\[[^\]|]+(?:\|[^\]]*)?\]\]|\*\*[^*]+\*\*|\*[^*]+\*|==[^=]+==)/g);
      return (
        <span key={k}>
          {parts.map((part, j) => {
            const imgMatch = part.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
            if (imgMatch) {
              const alt = imgMatch[1];
              const src = imgMatch[2].startsWith('http') ? imgMatch[2] : `/attachments/${imgMatch[2]}`;
              return <img key={j} src={src} alt={alt} style={{ maxWidth: '100%', borderRadius: 'var(--radius-md)', margin: '8px 0', display: 'block' }} />;
            }

            // Match [[id]] or [[id|display text]]
            const wikiMatch = part.match(/^\[\[([^\]|]+)(?:\|([^\]]*))?\]\]$/);
            if (wikiMatch) {
              const linkId = wikiMatch[1];
              const displayText = wikiMatch[2] || null;
              const linked = allZettel.find(z => z.id === linkId);
              let label;
              if (displayText) {
                label = displayText;
              } else if (linked?.title) {
                label = `${linkId}: ${linked.title}`;
              } else {
                label = linkId;
              }
              return (
                <span key={j} className="link-item" onClick={() => onNavigate(linkId)} style={{ cursor: 'pointer' }}>
                  {label}
                </span>
              );
            }
            const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
            if (boldMatch) return <strong key={j}>{boldMatch[1]}</strong>;

            const italicMatch = part.match(/^\*([^*]+)\*$/);
            if (italicMatch) return <em key={j}>{italicMatch[1]}</em>;

            const highlightMatch = part.match(/^==([^=]+)==$/);
            if (highlightMatch) return (
              <mark key={j} style={{
                background: 'var(--highlight-bg)', color: 'var(--color-text)',
                borderRadius: 3, padding: '1px 3px'
              }}>
                {highlightMatch[1]}
              </mark>
            );

            return <span key={j}>{part}</span>;
          })}
        </span>
      );
    };

    for (const line of lines) {
      if (line.startsWith('> ')) {
        flushList();
        bqLines.push(line.slice(2));
      } else if (line === '>') {
        flushList();
        bqLines.push('');
      } else if (/^(\s*)[-*]\s+/.test(line)) {
        flushBlockquote();
        if (listItems.length > 0 && listItems[0].ordered) flushList();
        const match = line.match(/^(\s*)[-*]\s+(.*)/);
        const depth = Math.floor((match[1] || '').length / 2);
        listItems.push({ depth, content: match[2], ordered: false });
      } else if (/^(\s*)\d+\.\s+/.test(line)) {
        flushBlockquote();
        if (listItems.length > 0 && !listItems[0].ordered) flushList();
        const match = line.match(/^(\s*)(\d+)\.\s+(.*)/);
        const depth = Math.floor((match[1] || '').length / 2);
        listItems.push({ depth, content: match[3], ordered: true, number: parseInt(match[2]) });
      } else {
        flushBlockquote();
        flushList();
        elements.push(<span key={key}>{renderInline(line, key)}<br /></span>);
        key++;
      }
    }
    flushBlockquote();
    flushList();

    return <div>{elements}</div>;
  };

  const handleImageDrop = async (files, editorView) => {
    for (const file of files) {
      try {
        const filename = await uploadImage(file);
        const md = `\n![${file.name.replace(/\.[^.]+$/, '')}](${filename})\n`;
        const pos = editorView.state.selection.main.head;
        editorView.dispatch({ changes: { from: pos, insert: md } });
      } catch (err) { console.error('Upload failed:', err); }
    }
  };

  return (
    <div className="zettel-editor">
      <div className={`editor-body${editorHlDragOver ? ' hl-editor-drop-active' : ''}`}
        onDragOver={handleEditorHlDragOver}
        onDragLeave={handleEditorHlDragLeave}
        onDrop={handleEditorHlDrop}
        onClick={(e) => {
        // Click on empty space in editor-body → focus the CodeMirror editor
        const isEmptyArea = e.target.classList.contains('editor-body') ||
          e.target.closest('.live-editor-container');
        if (isEmptyArea && !e.target.closest('.cm-content') && !e.target.closest('.view-links') && !e.target.closest('input')) {
          if (editorRef.current) {
            editorRef.current.focus();
          }
        }
      }}>
        {/* Unternotizen — collapsible dropdown above title */}
        {(() => {
          const children = sortByLuhmann(getDirectChildren(allZettel, zettel.id));
          const hasChildren = children.length > 0;
          if (hasChildren) {
            return (
              <details className="view-subnotes">
                <summary className="subnotes-header">
                  <span className="subnotes-header-arrow">▶</span>
                  <span>Unternotizen</span>
                  <span className="subnotes-count">{children.length}</span>
                  {!readOnly && (
                    <button
                      className="subnotes-add-btn"
                      onClick={(e) => { e.stopPropagation(); handleBranch(); }}
                      title="Neuen Unterzettel anlegen"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                  )}
                </summary>
                <div className="subnotes-list">
                  {children.map(child => (
                    <button key={child.id} className="backlink-item" onClick={() => onNavigate(child.id)}>
                      <span className="backlink-id">{child.id}</span>
                      <span className="backlink-title">{child.title || '(Ohne Titel)'}</span>
                    </button>
                  ))}
                </div>
              </details>
            );
          } else {
            // No children — non-collapsible row with count 0
            return (
              <div className="view-subnotes view-subnotes--empty">
                <div className="subnotes-header subnotes-header--empty">
                  <span className="subnotes-header-arrow" style={{ opacity: 0.25 }}>▶</span>
                  <span>Unternotizen</span>
                  <span className="subnotes-count">0</span>
                  {!readOnly && (
                    <button
                      className="subnotes-add-btn"
                      onClick={handleBranch}
                      title="Neuen Unterzettel anlegen"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          }
        })()}

        {/* Breadcrumb: Vorfahren-Hierarchie */}
        {(() => {
          const ancestors = [];
          let pid = getParentId(zettel.id);
          while (pid) {
            const ancestor = allZettel.find(z => z.id === pid);
            if (ancestor) ancestors.unshift(ancestor);
            pid = getParentId(pid);
          }
          if (ancestors.length === 0) return null;
          return (
            <nav className="ze-breadcrumb" aria-label="Zettel-Hierarchie">
              {ancestors.map((ancestor, i) => (
                <React.Fragment key={ancestor.id}>
                  <span
                    className="breadcrumb-segment clickable"
                    onClick={() => onNavigate && onNavigate(ancestor.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && onNavigate && onNavigate(ancestor.id)}
                  >
                    <span className="breadcrumb-id">{ancestor.id}</span>
                    <span className="breadcrumb-title">{ancestor.title || '(Ohne Titel)'}</span>
                  </span>
                  {i < ancestors.length - 1 && <span className="breadcrumb-sep" aria-hidden="true">›</span>}
                </React.Fragment>
              ))}
              <span className="breadcrumb-sep" aria-hidden="true">›</span>
              <span className="breadcrumb-segment breadcrumb-current" aria-current="page">
                <span className="breadcrumb-id">{zettel.id}</span>
              </span>
            </nav>
          );
        })()}

        <input
          ref={titleInputRef}
          className="editor-title-input"
          value={title}
          onChange={readOnly ? undefined : e => setTitle(e.target.value)}
          placeholder="Titel..."
          readOnly={readOnly}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (autoSaveRef.current) {
                clearTimeout(autoSaveRef.current);
                autoSaveRef.current = null;
              }
              updateZettel(zettel.id, {
                title,
                content,
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                links: links.split(',').map(l => l.trim()).filter(Boolean),
              }).then(saved => { onSaved(saved); });
              if (onNewNoteSaved) onNewNoteSaved();
              setTimeout(() => { editorRef.current?.focus(); }, 0);
            }
          }}
        />
        {newNoteMode && (
          <p className="ze-new-note-hint">
            <kbd>Enter</kbd> speichert den Titel — dann geht&apos;s in den Inhalt
          </p>
        )}

        <div className="view-meta">
          <span className="meta-item">
            <span className="meta-label">Geändert: </span>{zettel.modified}
          </span>
          {saveStatus && (
            <span className="ze-save-status">
              {saveStatus === 'saving' ? 'Speichert…' : '✓ Gespeichert'}
            </span>
          )}
        </div>

        {/* Tags */}
        {zettel.tags && zettel.tags.length > 0 && (
          <div className="zettel-item-tags" style={{ marginBottom: 16 }}>
            {zettel.tags.map(t => <span key={t} className="tag">{t}</span>)}
          </div>
        )}

        {/* Live Preview Editor */}
        <LiveEditor
          ref={editorRef}
          value={content}
          onChange={readOnly ? undefined : setContent}
          onDrop={readOnly ? undefined : handleImageDrop}
          cursorAt={zettel?._cursorAt}
          highlightQuote={zettel?._highlightQuote}
          allZettel={allZettel}
          onNavigate={onNavigate}
          onOpenInBibliothek={onOpenReadwise}
          onRemoveHighlight={readOnly ? undefined : handleRemoveHighlight}
          onDragOutHighlight={readOnly ? undefined : doRemoveHighlight}
          onMoveHighlight={readOnly ? undefined : handleMoveHighlight}
          getHighlightData={getHighlightData}
          readOnly={readOnly}
        />

        {/* Eingehende Links (Backlinks) — collapsible dropdown */}
        {(() => {
          const backlinks = allZettel.filter(z =>
            z.id !== zettel.id && (
              (z.links || []).includes(zettel.id) ||
              (z.wikilinks || []).includes(zettel.id)
            )
          );
          if (backlinks.length === 0) return null;
          return (
            <details className="view-backlinks" onToggle={(e) => {
                if (e.currentTarget.open) {
                  const el = e.currentTarget;
                  requestAnimationFrame(() => requestAnimationFrame(() => {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }));
                }
              }}>
              <summary className="backlinks-header">
                <span className="backlinks-header-arrow">▶</span>
                <span>Eingehende Links</span>
                <span className="backlinks-count">{backlinks.length}</span>
              </summary>
              <div className="backlinks-list">
                {backlinks.map(z => (
                  <button key={z.id} className="backlink-item" onClick={() => onNavigate(z.id)}>
                    <span className="backlink-id">{z.id}</span>
                    <span className="backlink-title">{z.title || '(Ohne Titel)'}</span>
                  </button>
                ))}
              </div>
            </details>
          );
        })()}
      </div>
    </div>
  );
}

// PERF: React.memo verhindert Re-Render bei Sidebar-Collapse und anderen App-State-Änderungen
export default React.memo(ZettelEditor);
